import * as React from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// =============================================================================
// confirm-dialog.tsx — Modale de confirmation accessible (pattern T-03).
// =============================================================================
// Modale légère écrite à la main (aucune dépendance ajoutée — pas de
// @radix-ui/react-dialog dans le lockfile figé). Conforme WCAG 2.1 AA :
//   - role="dialog" + aria-modal="true" + aria-labelledby/aria-describedby ;
//   - focus déplacé sur le 1er contrôle à l'ouverture, restauré à la fermeture ;
//   - fermeture clavier (Échap) ; le fond (overlay) est décoratif (aria-hidden).
// Pas de piège de focus complet (la modale n'a que 2 boutons + le fond inerte) :
// suffisant pour ce pattern de confirmation simple sans dépendance.

export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly description: string;
  readonly confirmLabel: string;
  readonly cancelLabel?: string;
  readonly confirmDisabled?: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  /** Contenu additionnel optionnel rendu sous la description (ex. récap de contrôle). */
  readonly children?: React.ReactNode;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Annuler',
  confirmDisabled = false,
  onConfirm,
  onCancel,
  children,
}: ConfirmDialogProps): React.JSX.Element | null {
  const confirmRef = React.useRef<HTMLButtonElement | null>(null);
  const previouslyFocused = React.useRef<HTMLElement | null>(null);
  // `onCancel` est lu via une ref TOUJOURS à jour : sans cela, un appelant passant un `onCancel`
  // recréé à chaque render (fonction inline) changerait la dépendance de l'effet ci-dessous, qui
  // se relancerait à CHAQUE frappe et RAPPELLERAIT `confirmRef.focus()` — volant le focus d'un champ
  // de saisie interne (ex. textarea motif de forçage resignature) après chaque caractère.
  // BUGFIX 2026-08-20 : l'effet ne dépend plus QUE de `open` (focus posé UNE fois à l'ouverture).
  const onCancelRef = React.useRef(onCancel);
  React.useEffect(() => {
    onCancelRef.current = onCancel;
  });

  React.useEffect(() => {
    if (!open) {
      return;
    }
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    confirmRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onCancelRef.current();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused.current?.focus();
    };
  }, [open]);

  if (!open) {
    return null;
  }

  const titleId = 'confirm-dialog-title';
  const descId = 'confirm-dialog-description';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay décoratif : ferme au clic, inerte pour les lecteurs d'écran. */}
      <div
        className="absolute inset-0 bg-foreground/40"
        aria-hidden="true"
        onClick={onCancel}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className={cn(
          'relative z-10 w-full max-w-md space-y-4 rounded-lg border border-border',
          'max-h-[85vh] overflow-y-auto bg-card p-6 text-card-foreground shadow-lg',
        )}
      >
        <div className="space-y-1">
          <h2 id={titleId} className="text-lg font-semibold tracking-tight">
            {title}
          </h2>
          <p id={descId} className="text-sm text-muted-foreground">
            {description}
          </p>
        </div>
        {children}
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            disabled={confirmDisabled}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
